import React, { memo, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { dates, formats, validators } from 'investira.sdk';
import { Button, Stack, InputAdornment, MenuItem, TextField, Icon, LoadingButton, Skeleton, ListItemText, Chip } from 'investira.react.components';
import { VALIDATIONS } from '../../../const';
import { DecimalTextField } from '../../../components/molecules';
import { useRelatorioContext } from '../../../contexts/RelatorioContext';
import services from '../../../services';
import withResponseHandling from '../../../hoc/withResponseHandling';
import withSaveParams from '../../../hoc/withSaveParams';
const VALIDATION_SCHEMA = Yup.object().shape({
  carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
  data: Yup.string().required(VALIDATIONS.REQUIRED),
  juros_delta: Yup.number().min(-10.0, 'Minímo permitido é -10,0%').max(10.0, 'Máximo permitido é 10,0%').required(VALIDATIONS.REQUIRED)
});
const DurationParams = memo(props => {
  const {
    state,
    actions
  } = useRelatorioContext();
  const navigate = useNavigate();
  const location = useLocation();
  const storedParams = props.storedParams('duration', props.lastOutput);
  console.log('storedParams', storedParams);
  const [datas, setDatas] = useState([]);
  const [alignment] = React.useState('favoritas');
  const {
    carteira_id,
    data,
    juros_delta
  } = storedParams;
  const xInitialValues = {
    carteira_id: carteira_id || '',
    data: data || null,
    juros_delta: juros_delta || 1
  };
  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.duration({
        ...pValues
      }, rRes => {
        const {
          output_cache_id
        } = rRes.data;
        navigate(`${location.pathname}/${output_cache_id}`);
        actions.handleNewConsultCancel();
        props.saveParams('duration', pValues);
      }, rErr => {
        props.responseErrorHandling(rErr);
      }, () => {
        pActions.setSubmitting(false);
      });
    }
  });
  const handleNumberChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };
  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const {
        carteira_id,
        juros_delta
      } = xValues;
      formik.setFieldValue('carteira_id', carteira_id);
      formik.setFieldValue('juros_delta', juros_delta);
    }
  }, []);
  useEffect(() => {
    if (state.isDialogOpen) {
      setDatas([]);
      formik.values.carteira_id && services.posicoes.datas({
        sort: 'data DESC',
        carteira_id: formik.values.carteira_id
      }, rRes => {
        const xDates = rRes.data.map(xPosicao => {
          return {
            value: formats.formatDateCustom(dates.toDate(xPosicao.data), 'YYYY-MM-DD'),
            label: formats.formatDateCustom(dates.toDate(xPosicao.data), 'DD/MM/YYYY'),
            aberta: xPosicao.aberta
          };
        });
        setDatas(xDates);
        const xValues = storedParams;
        if (!validators.isEmpty(xValues)) {
          const {
            data
          } = xValues;
          formik.setFieldValue('data', data);
        } else {
          formik.setFieldValue('data', xDates[0].value);
        }
      }, rErr => {
        props.responseErrorHandling(rErr);
      });
    }
  }, [state.isDialogOpen, formik.values.carteira_id]);

  // Define a lista de carteiras em ordem alfabetica
  let xCarteiras = useMemo(() => props.carteiras.slice().sort((a, b) => a.carteira.localeCompare(b.carteira)), [props.carteiras]);

  // Define filtro de carteiras baseado no alignment selecionado (favoritas, outras, todas)
  xCarteiras = useMemo(() => {
    if (alignment === 'favoritas') {
      return props.carteiras.filter(xCarteira => xCarteira.favorito === 1).sort((a, b) => a.carteira.localeCompare(b.carteira));
    } else if (alignment === 'outras') {
      return props.carteiras.filter(xCarteira => xCarteira.favorito === 0).sort((a, b) => a.carteira.localeCompare(b.carteira));
    } else {
      // "todas"
      return props.carteiras.filter(xCarteira => xCarteira.favorito === 1 || xCarteira.favorito === 0).sort((a, b) => a.carteira.localeCompare(b.carteira));
    }
  }, [props.carteiras, alignment]);
  console.log('formik.values', formik.values);
  return <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{
      marginBottom: 2,
      py: 1
    }} spacing={2}>
        <Stack flexGrow={1}>
          <TextField id="carteira_id" name="carteira_id" label="Carteira" variant="outlined" value={formik.values.carteira_id} onChange={formik.handleChange} select fullWidth InputProps={{
          startAdornment: <InputAdornment position="start">
                  <Icon iconName="wallet" size={21} />
                </InputAdornment>
        }}>
            {xCarteiras.map(xOption => <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                {xOption.carteira}
              </MenuItem>)}
          </TextField>
        </Stack>

        {formik.values.carteira_id && <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? <Skeleton variant="rounded" animation="wave" height={32} /> : <TextField id="data" name="data" label="Posição de" variant="outlined" value={formik.values.data} onChange={formik.handleChange} sx={{
              '& .MuiChip-root': {
                display: 'none'
              }
            }} select InputProps={{
              startAdornment: <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
            }} error={Boolean(formik.errors.data)} helperText={formik.errors.data} disabled={datas.length <= 1}>
                    {datas.map((xOption, xIndex) => <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && <Chip icon={<Icon iconName="box_3d_open" size={18} />} label={'Aberta'} color="warning" size="small" sx={{
                  px: 1
                }} variant="filled" />}
                      </MenuItem>)}
                  </TextField>}
              </Stack>
            </Stack>

            <Stack>
              <DecimalTextField id="juros_delta" name="juros_delta" label="Juros Delta" variant="outlined" value={formik.values.juros_delta} placeholder={'1,0'} onChange={(_, pValue) => handleNumberChange('juros_delta', pValue)} decimal={2} allowNegative={true} InputLabelProps={{
            shrink: true
          }} InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }} error={Boolean(formik.errors.juros_delta)} helperText={formik.errors.juros_delta} fullWidth />
            </Stack>

            {/* <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.aberta}
                    onChange={(e, value) => {
                      handleSwitchChange("aberta", value);
                    }}
                    name="aberta"
                    disabled={true}
                  />
                }
                label="Posição indireta"
              />
             </Stack> */}
          </>}

        <Stack alignItems="center" justifyContent="flex-end" direction="row" spacing={2}>
          <Button variant="outlined" type="button" color="primary" onClick={actions.handleNewConsultCancel}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" type="submit" loading={formik.isSubmitting} disabled={formik.isSubmitting || !formik.isValid || validators.isEmpty(formik.values.carteira_id)}>
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>;
});
export default withResponseHandling(withSaveParams(DurationParams));
